export { lazyWithRetry, isChunkLoadErrorEvent } from './lazyWithRetry'
export { parseApiDate, rotuloDaCompetencia } from './format'
export { collectMembershipRoles, savableMembershipRoles } from './membershipRoles'
export type { MembershipRolesLoad } from './membershipRoles'
export { DEFAULT_VM_USER, DEFAULT_WINDOWS_USER } from './vmCredentials'
export { isLocalhostHostname } from './devHost'
export {
  PORTES,
  SETORES,
  normalizeDocument,
  onlyDigits,
  tipoDeDocumentoValido,
  isValidCpfCnpj,
  isValidPhone,
  formatDocument,
  formatCep,
  formatPhone,
} from './dadosCadastrais'
export type { Porte, Setor } from './dadosCadastrais'
export {
  SENTRY_DSN_PADRAO,
  SENTRY_ENVIRONMENT_DESCONHECIDO,
  resolveSentryConfig,
} from './sentryConfig'
export type { SentryEnv, SentryRuntimeConfig } from './sentryConfig'
export {
  BACKUP_CONCLUIDO_STATUSES,
  isBackupConcluido,
  FILTRO_DE_BACKUP_CONCLUIDO,
} from './backupStatus'
export { isVMRemovida } from './vmRemovida'
export {
  CLUSTER_STATUSES,
  CLUSTER_STATUSES_ADMINISTRATIVOS,
  isClusterStatusAdministrativo,
  statusDoClusterParaEnvio,
  clusterStatusLabel,
} from './clusterStatus'
export type { ClusterStatus, ClusterStatusAdministrativo } from './clusterStatus'
export {
  WAN_VLAN_TAG_MAX,
  WAN_VLAN_TAG_MIN,
  wanVlanTagParaEnvio,
  wanVlanTagParaFormulario,
} from './wanVlanTag'
export type { WanVlanTagParaEnvio } from './wanVlanTag'
export {
  TIPOS_DE_PRODUTO_DO_ESCOPO,
  tiposDoEscopo,
  faltaAlvo,
  camposDoEscopo,
  produtosDoEscopo,
  rotuloDoEscopo,
} from './specialContractScope'
export type { ModalidadeDoContrato, EscopoDoContrato } from './specialContractScope'
export type { VMDeJobDeBackup } from './vmRemovida'
export {
  AVISO_PROVISIONAMENTO_INCOMPLETO,
  provisionamentoIncompleto,
  desdeQuandoIncompleto,
} from './provisionamentoIncompleto'
export type { VMComProvisionamento } from './provisionamentoIncompleto'
export {
  EM_BRANCO,
  INCOMPARABLE_REASONS,
  formatShortPeriod,
  formatMoney,
  formatUnitRate,
  summarizeBilled,
  isBilledComparable,
  incomparableReasonText,
} from './contractBilling'
export type { BilledInvoiceRef, BilledSummary, BilledDirection } from './contractBilling'

export const cn = (...classes: (string | undefined | null | false)[]) => {
  return classes.filter(Boolean).join(' ')
}

/**
 * O Pydantic v2 carimba "Value error, " na frente da mensagem levantada por um
 * validador nosso. É ruído de framework: quem lê é o cliente final.
 */
const limpaPrefixoPydantic = (msg: string): string =>
  msg.replace(/^(Value error|Assertion failed),\s*/i, '')

/**
 * O texto que a API mandou, ou `null` quando ela não mandou nenhum.
 *
 * Separado de `getErrorMessage` porque a distinção importa em quem decide entre
 * a frase do servidor e uma orientação própria da tela. `getErrorMessage` nunca
 * devolve vazio — sem texto da API ele cai no `error.message` do axios
 * ("Request failed with status code 402"), que não é frase para o cliente ler.
 * Quem quisesse esse "veio da API?" reimplementava a extração e as duas cópias
 * divergiam; agora há uma fonte só.
 *
 * @param error - Erro do axios ou objeto de erro
 */
export const getApiErrorText = (error: any): string | null => {
  // Erro de validação (422): a mensagem útil está em `details[].msg`, não em
  // `message` — este último é sempre o genérico "Request validation failed"
  // (backend/app/main.py). Preferi-lo escondia do cliente a única frase que
  // dizia o que corrigir: era o que acontecia ao tentar criar VM com um nome de
  // usuário recusado (#226).
  // Amarrado ao `validation_error` (não a "tem details com msg"): `details` é um
  // campo genérico, e um erro futuro que o use para texto técnico não deve
  // vazar para o cliente por causa deste ramo.
  const errorBody = error?.response?.data?.error
  const validationDetails = errorBody?.details
  if (errorBody?.type === 'validation_error' && Array.isArray(validationDetails)) {
    const mensagens = validationDetails
      .map((d: any) => (typeof d?.msg === 'string' ? limpaPrefixoPydantic(d.msg) : null))
      .filter(Boolean)
    if (mensagens.length > 0) {
      return mensagens.join('\n')
    }
  }

  // Corpo devolvido como texto puro (proxy, gateway, erro fora do handler).
  if (typeof error?.response?.data === 'string' && error.response.data) {
    return error.response.data
  }

  // Se o erro tem response.data.error.message (formato padrão da nossa API)
  if (error?.response?.data?.error?.message) {
    return error.response.data.error.message
  }

  // Se o erro tem response.data.message (formato alternativo)
  if (error?.response?.data?.message) {
    return error.response.data.message
  }

  // Se o erro tem response.data.detail (formato FastAPI validation error)
  if (error?.response?.data?.detail) {
    // detail pode ser string ou objeto com message
    const detail = error.response.data.detail
    if (typeof detail === 'string') {
      return detail
    }
    if (detail?.message) {
      return detail.message
    }
    return JSON.stringify(detail)
  }

  return null
}

/**
 * Código estruturado da recusa, quando a API mandou um (`detail` em dict vira
 * `error.code` pelo handler global do backend). Existe para quem precisa REAGIR
 * a uma recusa específica sem casar substring de mensagem — texto muda, `code`
 * não. Ex.: `vpc_without_gateway` (issue #727).
 *
 * @param error - Erro do axios ou objeto de erro
 */
export const getApiErrorCode = (error: any): string | null =>
  error?.response?.data?.error?.code ?? null

/**
 * Extrai a mensagem de erro de diferentes formatos de resposta da API
 * @param error - Erro do axios ou objeto de erro
 * @returns Mensagem de erro user-friendly (nunca vazia)
 */
export const getErrorMessage = (error: any): string => {
  const daApi = getApiErrorText(error)
  if (daApi !== null) {
    return daApi
  }

  // Se o erro tem message diretamente
  if (error?.message) {
    return error.message
  }

  // Se o erro é uma string
  if (typeof error === 'string') {
    return error
  }

  // Fallback para erros não estruturados
  return 'Ocorreu um erro inesperado. Tente novamente.'
}
export { describeOwnershipPreview, ownershipTransferMarkerLabel } from './vmOwnership'
export type {
  OwnershipOperatorType,
  OwnershipPreview,
  OwnershipTransferMarker,
} from './vmOwnership'
export {
  PUBLIC_IP_BILLING_MODES,
  acoesDoIp,
  descreverCobrancaDaDedicacao,
  descreverEstorno,
  rotuloDaCobranca,
  rotuloDoModoDeCobranca,
  rotuloDoStatusDaVm,
  rotuloDoUso,
  usoDoIp,
} from './publicIpPool'
export {
  PUBLIC_IP_HISTORY_ACTIONS,
  PUBLIC_IP_SAIDAS_DO_POOL,
  autorDoEvento,
  momentoDoEvento,
  resumoDaCobrancaDoCiclo,
  rotuloDaAcaoDoHistorico,
  rotuloDaOrigemDoCiclo,
  rotuloDoDonoDoCiclo,
  rotuloDaSaidaDoPool,
  rotuloDoPeriodoDoCiclo,
  valorDoEvento,
} from './publicIpHistory'
export type {
  PublicIPHistoryAction,
  PublicIPHistoryAviso,
  PublicIPHistoryEvent,
  PublicIPHistoryResponse,
  PublicIPOwnershipCycle,
  PublicIPRemovedItem,
  PublicIPRemovedListResponse,
} from './publicIpHistory'
export type {
  PublicIPAllocatePreview,
  PublicIPAllocateRequest,
  PublicIPAllocationBillingMode,
  PublicIPAllocationTargets,
  PublicIPPoolEntry,
  PublicIPRecusa,
  PublicIPRefundPreview,
  PublicIPReleasePreview,
  PublicIPReleaseRequest,
  PublicIPUso,
} from './publicIpPool'
export {
  ROTULO_DA_ETAPA,
  ROTULO_DO_ESTADO,
  ROTULO_DO_MOTIVO,
  ETAPAS_EM_ANDAMENTO,
  ESTADOS_FINAIS,
  SEM_RESPOSTA_APOS_MS,
  JANELA_DE_RETOMADA_MS,
  estadoDaAtualizacao,
  resumoDoLote,
  agentsParaRetomar,
  suportaAtualizacaoRemota,
  contaAtualizaveisEmMassa,
  classificaDesatualizados,
} from './agentUpdateStatus'
export type {
  DesatualizadosClassificados,
  EstadoDaAtualizacao,
  ResumoDoLote,
} from './agentUpdateStatus'
export { RESOURCE_TYPE_LABELS, rotuloDoTipoDeRecurso } from './resourceTypeLabels'
export {
  ROTULO_DO_ACUMULO,
  DESCRICAO_DO_ACUMULO,
  ROTULO_DA_LIBERACAO,
  ROTULO_DA_QUITACAO_ATRASADA,
  ROTULO_DO_LANCAMENTO_DO_VOUCHER,
  rotuloDoLancamentoDoVoucher,
  rotuloDoVoucherNoExtrato,
  sinalDoLancamentoDoVoucher,
  VALOR_MAXIMO_DO_VOUCHER,
  TOLERANCIA_MAXIMA_EM_DIAS,
  descontoDoVoucher,
  validarTermosDoVoucher,
  MOTIVO_DA_QUITACAO_NAO_APLICADA,
  linhasDaMemoriaDoVoucher,
  formatarViradaDoVoucher,
  formatarDiaDoVoucher,
  inicioDoDiaEmBrasilia,
  fimDoDiaEmBrasilia,
  diaEmBrasilia,
  formularioDoVoucher,
  validarFormularioDoVoucher,
  payloadDoVoucher,
  mudancasDoVoucher,
  descricaoDasMudancasDoVoucher,
  competenciaDoLancamentoDoVoucher,
} from './voucher'
export type {
  AcumuloDoVoucher,
  LiberacaoDoVoucher,
  TermosDoVoucherNoFormulario,
  ErrosDoVoucher,
  MemoriaDoVoucher,
  FormularioDoVoucher,
  TermosDoVoucherGravados,
  CorpoDoVoucher,
} from './voucher'
